import React, { useCallback } from 'react'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStickyFooter from '../../OfferPageStickyFooter'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isRoomsInvalid } from 'lib/search/searchUtils'

interface Props {
  offer: App.Offer;
  rooms: Array<App.RoomOccupants>;
  checkIn: moment.Moment;
  isFLightSelected: boolean;
  visible: boolean;
  lowestPricePackage: App.Package | App.BundlePackageWithPrice;
}

function HotelOfferPageStickyFooterVariant(props: Props) {
  const {
    offer,
    rooms,
    checkIn,
    isFLightSelected,
    visible,
    lowestPricePackage,
  } = props

  const offerPageDispatch = useOfferPageDispatch()

  const pricePackage = useOfferPackagePrice(lowestPricePackage, offer)
  const shouldShowRate = pricePackage.valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowValue = getHotelOfferDisplayedValue(!!offer.bundledWithFlightsOnly, pricePackage.totalPrice, pricePackage.totalValue, !!offer.shouldDisplayValue)

  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)
  const canViewOptions = !!checkIn && !invalidRoomSelection

  const ctaLabel = canViewOptions ? 'View options' : 'Check availability'
  const onCTAClick = useCallback(() => {
    if (invalidRoomSelection) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        flights: isFLightSelected,
      })
      return
    }

    if (!checkIn) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        flights: isFLightSelected,
      })

      return
    }

    // Reset previous room selected
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomSelection' })
  }, [checkIn, invalidRoomSelection, isFLightSelected, offerPageDispatch])

  return (
    <OfferPageStickyFooter visible={visible} onCTAClick={onCTAClick} callToActionCopy={ctaLabel}>
      <div>
        <PriceRowCaption>
          {lowestPricePackage.durationLabel} from
        </PriceRowCaption>
        <PriceRowPrice
          size="M"
          price={pricePackage.totalPrice}
          saleUnit={pricePackage.saleUnit}
        />
        {shouldShowValue && shouldShowRate && <PriceRowValueDiscountWithCaption
          size="L"
          originalValue={Math.ceil(pricePackage.totalValue)}
          discountPercentage={pricePackage.valueOffPercent}
        />}
      </div>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: appState.offer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  isFLightSelected: state.flights,
}))(HotelOfferPageStickyFooterVariant)
